import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Database } from 'lucide-react';
import { datasetRowIdColumn, isInternalDatasetColumn } from '@cotera/sdk/core';
import {
  Watchable,
  useAction,
  useWatchableValue,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { useGetAccessToken } from '@cotera/client/app/hooks/use-access-token';
import { Icon, Text, Tooltip, toast } from '@cotera/client/app/components/ui';
import {
  DataGrid,
  DataGridHeader,
  createDataGridViewModel,
  dataGridColumnTypeFromSqlType,
  DataGridFilterChip,
  DataGridSortChips,
  DataGridCopyChip,
  DataGridExpandChip,
  DataGridHiddenColumnsChip,
  type DataGridCellComponentProps,
  DataGridEmptyState,
  DataGridHighlightContext,
  type DataGridColumn,
  type DataGridEmptyStateProps,
  type DataGridColumnDisplayOptions,
  type DataGridFooterProps,
  type DataGridHeaderComponentProps,
  type DataGridHeaderContext,
  type DataGridRowHeight,
  type DataGridRowId,
  type DataGridTopBarProps,
} from '@cotera/client/app/components/ui/data-grid';
import { cn } from '@cotera/client/v0/lib/utils';
import { Button } from '@cotera/client/v0/components/ui/button';
import { DropdownMenuItem } from '@cotera/client/v0/components/ui/dropdown-menu';
import type {
  DatasetArtifactMetadataOutput,
  DatasetArtifactOutput,
  DatasetOutput,
  DatasetRowCountOutput,
} from '@cotera/client/app/etc/api/typed-client';
import type { DatasetOperationColumnType } from '../dataset-columns';
import {
  DatasetAddColumnForm,
  DatasetEmptyState,
} from '../dataset-manual-controls';
import { useImportDatasetCsv } from '../use-import-dataset-csv';
import { emitLocalDatasetImport } from '../dataset-import';
import { RunDatasetPreviewQueryAction } from '../run-dataset-preview-query.action';
import { SaveDatasetAction } from '../save-dataset.action';
import { ViewCategoryBreakdownAction } from './view-category-breakdown.action';
import { useOptionalDatasetModel } from '../dataset.model';
import { useDatasetTheme } from '../dataset-theme';
import { isWarehouseDataset } from '../dataset-utils';
import { useLiveDatasetOperations } from './use-live-dataset-operations';
import { resolveDatasetParquetUrl } from './parquet-url';
import { DatasetPreviewTable } from './preview-table';
import type { DatasetOperationSkipReporter } from './operations-layer';
import { useAnalytics } from '@cotera/client/app/etc/metrics';
import { DEFAULT_DATASET_QUERY } from './source-sql';
import { datasetRowCountNote } from './row-count';
import type { DatasetRowIdCursor } from './row-id-cursor';
import { DatasetQueryBar } from './query-bar';
import { DatasetSearch } from './dataset-search';
import { useDatasetSearchHotkey } from './dataset-search-hotkey';
import { createDuckDbDatasetPreviewGridAdapter } from './adapter';
import type { DatasetPreviewGridAdapter } from './adapter';
import { useDatasetPreviewGridSource } from './grid-source';
import { useConfirmDeleteColumn } from './delete-column-confirm';
import { useRenameColumn } from './rename-column-prompt';
import { useConfirmDeleteRows } from './delete-rows-confirm';
import { DatasetPendingEdits } from './pending-edits';
import type {
  DatasetPreviewLayer,
  DatasetPreviewRow,
  DatasetPreviewStatus,
} from './types';
import { DuckDbLayerStack } from '@cotera/client/app/etc/duckdb/layers';

type DatasetPreviewSkeletonRow = { id: number };

const DatasetPreviewSkeletonCell = <TRow,>({
  style,
}: DataGridCellComponentProps<TRow>) => (
  <div
    role="gridcell"
    className="absolute flex h-full items-center border-b border-r border-border/70 px-3"
    style={style}
  >
    <div className="h-3 w-2/3 rounded bg-coco-accent animate-pulse" />
  </div>
);

/**
 * The skeleton's chrome: the column count and nothing else.
 *
 * Deliberately silent about rows. The default bar would count the placeholder
 * rows this grid invents — "12 rows loaded" — which is not a fact about the
 * dataset at all, and the real count is a `count(*)` that cannot run until the
 * parquet has arrived. Saying nothing until there is something true to say
 * beats replacing one number with a very different one a second later.
 */
const DatasetPreviewSkeletonTopBar = <TRow,>({
  columns,
}: DataGridTopBarProps<TRow>) => {
  const theme = useDatasetTheme();
  const visibleColumnCount = columns.filter(
    (column) => column.visible !== false
  ).length;

  return (
    <div
      className={cn(
        'flex min-h-9 items-center gap-2 border-b border-border px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground',
        theme.gridChrome
      )}
    >
      <span className="shrink-0 tabular-nums">
        {visibleColumnCount.toLocaleString()} columns
      </span>
    </div>
  );
};

const DatasetPreviewLoadingGrid: React.FC<{
  columns: DatasetArtifactMetadataOutput['schema']['columns'];
  /**
   * The leading columns the loaded grid will show, known from the layer stack
   * alone and so long before the parquet is materialised.
   *
   * Reserving their place here stops them appearing later and shifting every
   * other column sideways once the data arrives. Taken from the real stack
   * rather than described again — a hard-coded copy of the Workflows column's
   * id and width used to live here and had to be kept in step by hand.
   */
  reservedColumns?: readonly DataGridColumn<DatasetPreviewRow>[];
}> = ({ columns, reservedColumns = [] }) => {
  const theme = useDatasetTheme();
  const skeletonRows = React.useMemo<DatasetPreviewSkeletonRow[]>(
    () => Array.from({ length: 12 }, (_, index) => ({ id: index })),
    []
  );
  const rows = React.useMemo(
    () => Watchable.fromValue(skeletonRows),
    [skeletonRows]
  );
  // The loaded grid hides the internal bookkeeping columns, so a skeleton that
  // showed them would both misrepresent the dataset and shift the layout once
  // the real grid takes over.
  const visibleColumns = React.useMemo(
    () => columns.filter((column) => !isInternalDatasetColumn(column.name)),
    [columns]
  );
  const gridColumns = React.useMemo<
    DataGridColumn<DatasetPreviewSkeletonRow>[]
  >(
    () => [
      // Same id, header, width and pinning as the real ones, so the swap is
      // invisible. Only the value differs: a skeleton has nothing to show.
      ...reservedColumns.map((column) => ({
        id: column.id,
        header: column.header,
        width: column.width,
        pinned: column.pinned,
        computed: true,
        getValue: () => '',
      })),
      ...visibleColumns.map((column, index) => ({
        id: column.name,
        header: column.name,
        type: dataGridColumnTypeFromSqlType(column.type),
        typeLabel: column.type ?? undefined,
        width: index === 0 ? 180 : 150,
        getValue: () => '',
      })),
    ],
    [reservedColumns, visibleColumns]
  );
  const viewModel = React.useMemo(
    () =>
      createDataGridViewModel<DatasetPreviewSkeletonRow>({
        columns: gridColumns,
        totalRows: null,
        totalLoadedRows: skeletonRows.length,
      }),
    [gridColumns, skeletonRows.length]
  );

  // Nothing to draw a grid for yet: this dataset either has no columns of its
  // own or is about to resolve into the empty state, and a column-less grid
  // would flash in between.
  if (visibleColumns.length === 0) {
    // Shaped like the empty state it is about to become, so resolving does not
    // move anything on the page.
    return (
      <div
        className={cn(
          'border border-dashed p-8 text-center',
          theme.surfaceRadius,
          theme.surfaceBorder,
          theme.surface
        )}
      >
        <div className="mx-auto h-10 w-10 rounded-full bg-coco-accent animate-pulse" />
        <div className="mx-auto mt-4 h-5 w-48 rounded bg-coco-accent animate-pulse" />
        <div className="mx-auto mt-3 h-4 w-80 max-w-full rounded bg-coco-accent animate-pulse" />
        <div className="mx-auto mt-5 h-7 w-56 rounded bg-coco-accent animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1">
      <DataGrid
        // The grid sits flush inside the card, so it takes the card's own
        // radius rather than the DataGrid default rounding inside it.
        className={cn(theme.surface, theme.surfaceRadius)}
        rows={rows}
        getRowId={(row) => row.id}
        viewModel={viewModel}
        CellComponent={DatasetPreviewSkeletonCell}
        TopBarComponent={DatasetPreviewSkeletonTopBar}
      />
    </div>
  );
};

/**
 * Dataset-specific footer: the standard counts plus an ad-hoc DuckDB SELECT
 * against a CTE named `dataset`. The committed statement lives in the preview
 * view model; the query bar below keeps a local draft until it is run.
 */
const DatasetPreviewFooter: React.FC<
  DataGridFooterProps<DatasetPreviewRow> & {
    customSelectSql: Watchable<string>;
    queryError: ReadonlyWatchable<string | null>;
    queryable: boolean;
    /**
     * What the statement can name, for completions. The dataset's own schema
     * rather than the grid's columns: a layer contributes columns the grid
     * shows but the `dataset` CTE has never heard of.
     */
    queryColumns: DatasetArtifactMetadataOutput['schema']['columns'];
    onRunQuery: (query: string) => void;
    /**
     * Whether the applied statement differs from the one saved on the dataset;
     * permanently false where a query is never persisted (the row picker's
     * grid). A watchable rather than a boolean for the reason given at
     * `FooterComponent`: the footer is a stable component type reading through a
     * ref, so the flag has to carry its own subscription to re-render on it.
     */
    queryDirty: ReadonlyWatchable<boolean>;
    onSaveQuery: () => void;
  }
> = ({
  columns,
  sorts,
  filters,
  clearSort,
  clearFilter,
  expandableCell,
  copyableSelection,
  customSelectSql,
  queryError,
  queryable,
  queryColumns,
  onRunQuery,
  queryDirty,
  onSaveQuery,
}) => {
  const theme = useDatasetTheme();
  const committed = useWatchableValue(customSelectSql);
  const error = useWatchableValue(queryError);
  const canSave = useWatchableValue(queryDirty);

  return (
    <div className={cn('border-t border-border', theme.gridChrome)}>
      <div className="flex items-center gap-3 border-t border-border/60 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
          {filters.map((filter) => (
            <DataGridFilterChip
              key={`filter-${filter.columnId}`}
              label={
                columns.find((column) => column.id === filter.columnId)?.id ??
                filter.columnId
              }
              value={filter.value}
              onClear={() => clearFilter(filter.columnId)}
            />
          ))}
          {/* Only where there is no query bar to carry it: when there is,
              the message belongs under the statement that caused it, not
              truncated in among the sorts and filters. */}
          {error !== null && !queryable ? (
            <span className="min-w-0 truncate text-destructive">{error}</span>
          ) : (
            // The sorts stay put while a re-read is in flight. They used to be
            // replaced by "Loading…", which meant that on every settled
            // keystroke of a search the chips vanished and came back — a flicker
            // reporting something the reader had not asked about, in the place
            // where they were reading how the grid is arranged. The body's
            // progress line already says a read is running.
            <DataGridSortChips
              columns={columns}
              sorts={sorts}
              clearSort={clearSort}
            />
          )}
        </div>
        {copyableSelection !== null && copyableSelection !== undefined ? (
          <DataGridCopyChip {...copyableSelection} />
        ) : null}
        {expandableCell !== null && expandableCell !== undefined ? (
          <DataGridExpandChip {...expandableCell} />
        ) : null}
      </div>
      {queryable ? (
        <DatasetQueryBar
          value={committed}
          error={error}
          columns={queryColumns}
          onRun={onRunQuery}
          canSave={canSave}
          onSave={onSaveQuery}
        />
      ) : null}
    </div>
  );
};

/**
 * What the body says when the search — not a filter — is what emptied it.
 *
 * The grid's own empty state can only name column filters, because they are the
 * only narrowing it can see. So a search that matched nothing blanked the table
 * and said "No rows", which on a dataset full of data reads as a broken table
 * rather than as an answer.
 *
 * The rule the grid's own empty state already sets: name the cause, and carry
 * the way out.
 */
export const DatasetSearchEmptyState: React.FC<{
  term: string;
  onClearSearch: () => void;
}> = ({ term, onClearSearch }) => (
  <div className="flex flex-col items-center gap-1.5 px-6 py-10 text-center">
    <Icon icon="magnifying-glass" className="text-muted-foreground/40" />
    <span className="text-sm font-medium text-foreground">
      No rows contain “{term}”
    </span>
    <span className="max-w-[24rem] text-[11px] leading-snug text-muted-foreground">
      Nothing in this dataset’s values matches it.
    </span>
    <button
      type="button"
      className="mt-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
      onClick={onClearSearch}
    >
      Clear search
    </button>
  </div>
);

/**
 * Dataset-specific top bar: the standard counts and hidden-column badges plus
 * the structural edits the grid itself cannot express — appending a row,
 * declaring a column, and replacing the whole dataset from a CSV. The first two
 * write through the dataset operation log.
 */
const DatasetPreviewTopBar: React.FC<
  DataGridTopBarProps<DatasetPreviewRow> & {
    /**
     * The term narrowing every column. A watchable rather than a string for the
     * reason given at `FooterComponent`: this is a stable component type read
     * through a ref, so the value has to carry its own subscription to
     * re-render on it.
     */
    search: Watchable<string>;
    /** Focus handle for the page's ⌘F. */
    searchInputRef: React.MutableRefObject<HTMLInputElement | null>;
    editable: boolean;
    /** Only manual datasets can have their contents replaced by a CSV. */
    canImportCsv: boolean;
    onAddRow: () => Promise<void>;
    onAddColumn: (input: {
      name: string;
      type: DatasetOperationColumnType;
    }) => Promise<void>;
    onImportCsv: (file: File) => Promise<void>;
    /** Rows changed by a live update since the last query. */
    driftedRowCount: number;
    onRefresh: () => void;
    /**
     * The dataset's own size, from the server. Null while it is still being
     * asked for, or when the dataset's kind declined to answer.
     */
    rowCount: DatasetRowCountOutput | null;
    /** Removes the picked rows; absent where the grid never writes. */
    onDeleteRows: (rowIds: readonly DataGridRowId[]) => Promise<void>;
    /** Drops the selection without touching the rows. */
    onClearSelection: () => void;
  }
> = ({
  columns,
  hiddenColumns,
  totalRows,
  totalLoadedRows,
  setColumnVisible,
  selectedRowIds,
  search,
  searchInputRef,
  editable,
  onAddRow,
  onAddColumn,
  driftedRowCount,
  onRefresh,
  rowCount,
  onDeleteRows,
  onClearSelection,
}) => {
  const theme = useDatasetTheme();
  const searchTerm = useWatchableValue(search);
  const [addingColumn, setAddingColumn] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirmDeleteRows = useConfirmDeleteRows();
  const visibleColumnCount = columns.filter(
    (column) => column.visible !== false
  ).length;
  const takenNames = React.useMemo(
    () => new Set(columns.map((column) => column.id)),
    [columns]
  );
  /**
   * What the count above is leaving out. The count itself stays the grid's own
   * — it is the only number that answers to the filters and the ad-hoc query,
   * and the only one that has seen the edits still sitting in the operation log
   * — so the server's figures are spent on the thing the browser cannot know:
   * how much of the dataset never reached it.
   */
  /**
   * The grid's own count once it has one, and what the artifact said about
   * itself until then.
   *
   * `totalRows` is null between mounting and the first page query returning, and
   * the alternative reading — `totalLoadedRows` — is whatever happens to have
   * been fetched by that moment, so the header would open on a number far below
   * the truth and then jump. The metadata is already the right answer for an
   * unfiltered grid, so there is nothing to jump from.
   */
  const shownRows = totalRows ?? rowCount?.artifactRows ?? null;
  const rowCountNote = datasetRowCountNote({ shownRows, rowCount });

  const handleAddRow = () => {
    setBusy(true);
    void onAddRow()
      // eslint-disable-next-line no-console
      .catch((error) => console.error('Failed to add row', error))
      .finally(() => setBusy(false));
  };

  const selectedCount = selectedRowIds.size;
  const handleDeleteRows = () => {
    void (async () => {
      if (!(await confirmDeleteRows(selectedCount))) {
        return;
      }
      setBusy(true);
      try {
        await onDeleteRows([...selectedRowIds]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete rows', error);
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    // A column, so the add-column form can drop to a row of its own instead of
    // replacing the toolbar: a form that evicts "Add row" and the selection
    // chip while it is open takes away the two things most likely to be wanted
    // next, and 400px of input and select never sat well in 36px of chrome.
    <div className={cn('border-b border-border', theme.gridChrome)}>
      {/* Three zones, in the order a reader uses them: what you ask, what is
        true right now, and what you can do. Spacing says so — `gap-x-3` between
        zones against the tighter gaps inside them — and so does behaviour: only
        the middle one changes size, so chips coming and going move neither the
        box being typed into nor the buttons being aimed at.

        It was one flat row of eight items with a `flex-1` spacer buried in the
        middle of it, so a chip appearing anywhere shifted everything after it,
        and wrapping split the row at whatever item happened to reach the edge.
        Wrapping now moves whole zones. */}
      <div className="@container/dataset-bar flex min-h-9 flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 text-[11px]">
        {/* Asking: the box, and the number that answers it. */}
        <div className="flex shrink-0 items-center gap-2">
          {/* One box for both halves of the dataset. It was two — a row search
          and a column finder side by side — which made the reader decide what
          kind of thing they were hunting before they had typed anything, and
          "account" is not a question they can always answer.

          First in the row, and not for emphasis. Everything else here changes
          width as the search does its work — the count loses digits, the sample
          note appears — and with any of it to the left the box slid out from
          under the cursor on the way to the answer. Nothing that moves may
          precede the thing being typed into. */}
          <DatasetSearch
            value={searchTerm}
            onChange={(value) => search.set(value)}
            inputRef={searchInputRef}
          />
          {/* Immediately after the box, because the count is its answer: what
          the reader typed and what it came to, side by side. */}
          <span className="flex shrink-0 items-center gap-1.5 uppercase tracking-wider tabular-nums text-muted-foreground">
            {/* Two numbers, and in a narrow pane only one of them survives.
              The row count answers the search and moves when the reader acts;
              the column count is standing context, and it is the wider of the
              two to print. Dropped rather than abbreviated — "10 COLS" saves a
              third of the width and costs the same glance. */}
            <span className="whitespace-nowrap">
              {shownRows !== null
                ? `${shownRows.toLocaleString()} rows`
                : `${totalLoadedRows.toLocaleString()} rows loaded`}
              {/* "of" once something is hidden: "10 columns" beside a
                "1 hidden" chip reads as ten in total with one put away, and it
                is eleven. */}
              <span className="hidden @5xl/dataset-bar:inline">
                {' · '}
                {hiddenColumns.length > 0
                  ? `${visibleColumnCount.toLocaleString()} of ${(
                      visibleColumnCount + hiddenColumns.length
                    ).toLocaleString()} columns`
                  : `${visibleColumnCount.toLocaleString()} columns`}
              </span>
            </span>
            {rowCountNote !== null ? (
              <Tooltip side="bottom" tooltipContent={rowCountNote} asChild>
                {/* Labelled rather than left to the hover card: the note is the
                only thing that says the grid is not the whole dataset, and a
                reader who cannot hover would otherwise never be told. */}
                <span
                  role="note"
                  aria-label={rowCountNote}
                  className="flex cursor-help items-center"
                >
                  <Icon
                    icon="information-circle"
                    size="small"
                    className="opacity-60"
                  />
                </span>
              </Tooltip>
            ) : null}
          </span>
        </div>

        {/* Reporting: what is true of the grid this moment and may not be the
          next — a selection, rows that drifted under a live update, columns
          being held back. All of it transient, all of it confined to the one
          zone that is allowed to change size, and truncated rather than
          allowed to push the zones either side of it.

          Not a place for "Loading…". The body draws a progress line across its
          top while a re-read is in flight, which says the same thing without
          occupying width — a label here arrived and left on every settled
          keystroke of a search, and took the row's right-hand half with it. */}
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden uppercase tracking-wider text-muted-foreground [&>*]:min-w-0">
          {/* Selection reads here rather than out of the action cluster's own
          label: the destructive action ends up nowhere near "Add row", and the
          cluster on the right keeps a fixed width instead of shifting every
          time the selection grows.

          One colour, one meaning. A primary-tinted pill with a red word inside
          read as two states arguing over the same chip; the only thing this
          does is delete, so it is destructive throughout and the count rides in
          the label rather than in a clause of its own. */}
          {editable && selectedCount > 0 ? (
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                className="flex shrink-0 items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/10 py-0.5 pl-2 pr-2.5 text-[11px] text-destructive transition-colors hover:bg-destructive/15 disabled:opacity-50"
                disabled={busy}
                onClick={handleDeleteRows}
              >
                <Icon icon="trash" size="small" />
                <span className="tabular-nums">
                  Delete {selectedCount.toLocaleString()}
                </span>
              </button>
              {/* Muted, not red: dropping a selection destroys nothing, and a
              second red control beside the first would claim it does. */}
              <Tooltip side="bottom" tooltipContent="Clear selection" asChild>
                <button
                  type="button"
                  aria-label="Clear selection"
                  className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  onClick={onClearSelection}
                >
                  <Icon icon="x-mark" size="small" />
                </button>
              </Tooltip>
            </div>
          ) : null}
          {/* Live updates change values in place without re-querying, so a row can
          end up out of order or past its filter. Offered rather than applied:
          the change was not user-initiated, and re-querying unprompted moves
          rows and resets scroll. */}
          {driftedRowCount > 0 ? (
            <button
              type="button"
              className="flex shrink-0 items-center gap-1 rounded-full border border-primary/40 bg-primary/10 py-0.5 pl-2 pr-2 text-[11px] text-foreground hover:bg-primary/20"
              onClick={onRefresh}
            >
              <Icon icon="arrow-path" size="small" className="opacity-70" />
              <span className="tabular-nums">
                {driftedRowCount.toLocaleString()}{' '}
                {driftedRowCount === 1 ? 'row' : 'rows'} changed
              </span>
              <span className="opacity-60">· Refresh</span>
            </button>
          ) : null}
          {/* One chip, not one per hidden column: a dataset with thirty columns
          and most of them hidden filled this bar with pills and pushed
          everything else off it. */}
          <DataGridHiddenColumnsChip
            columns={columns}
            setColumnVisible={setColumnVisible}
          />
        </div>
        {/* Doing: what writes to the dataset, against the right edge where a
          reader looks for verbs. Everything that only changes how it is drawn
          — row height, the CSV export — lives in the page's own menu now, so
          this bar holds nothing but the two constructive controls. */}
        <div className="flex shrink-0 items-center gap-1">
          {/* One tight pair, because they are one idea: adding structure to the
          grid. "Row"/"Column" rather than "Add row"/"Add column" — the icon
          already says add, and repeating the word pushes the only distinguishing
          part of each label to the end, which is the worst place to scan for it.
          The full phrasing stays as the accessible name.

          Bordered, where they used to be ghosts. The weight in this row was
          inverted: the hidden-columns chip reports a state nobody can click and
          carries a border and a fill, while the two controls that actually
          write to the dataset had neither until the pointer was already on
          them. The quiet rule that falls out is worth more than the borders —
          a pill is something the grid is telling you, a rectangle is something
          you can do to it — so the shape carries it, and no colour is spent.

          Flat, though: `outline` brings a shadow, and nothing else in this bar
          has one. */}
          {editable ? (
            <div className="flex shrink-0 items-center gap-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 shadow-none"
                aria-label="Add row"
                disabled={busy}
                onClick={handleAddRow}
              >
                <Icon icon="plus" size="small" />
                Row
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 shadow-none"
                aria-label="Add column"
                disabled={busy || addingColumn}
                onClick={() => setAddingColumn(true)}
              >
                <Icon icon="plus" size="small" />
                Column
              </Button>
            </div>
          ) : null}
        </div>
      </div>
      {editable && addingColumn ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-border/60 px-3 py-2">
          <DatasetAddColumnForm
            takenNames={takenNames}
            onSubmit={async (input) => {
              await onAddColumn(input);
              setAddingColumn(false);
            }}
            onCancel={() => setAddingColumn(false)}
          />
        </div>
      ) : null}
    </div>
  );
};

const DatasetPreviewGridBody: React.FC<{
  adapter: DatasetPreviewGridAdapter;
  canImportCsv: boolean;
  onStatusChange: (status: DatasetPreviewStatus) => void;
  onImportCsv: (file: File) => Promise<void>;
  layers: readonly DatasetPreviewLayer[];
  composedLayers: DuckDbLayerStack<DatasetPreviewRow>;
  readOnly?: boolean;
  /** Names the downloaded file, so it lands as the dataset rather than a uuid. */
  datasetName: string;
  /** The dataset's own size, for the top bar's sample disclosure. */
  rowCount: DatasetRowCountOutput | null;
  /**
   * Bumped every time the table underneath is refilled from a new artifact.
   *
   * The adapter deliberately keeps its identity across that — the whole point of
   * a dataset-scoped table — so the effect that reloads rows when the adapter
   * changes no longer fires for it. This is what asks for the new rows instead,
   * and it re-queries rather than rebuilding, so sorts, filters, selection and
   * scroll survive.
   */
  dataRevision: number;
  columnHeaderActions?: DatasetPreviewColumnHeaderActions;
  /** See {@link DatasetPreviewGridControls}; the view model lives down here. */
  onControlsChange?: (controls: DatasetPreviewGridControls | null) => void;
}> = ({
  adapter,
  canImportCsv,
  onStatusChange,
  onImportCsv,
  layers,
  composedLayers,
  readOnly = false,
  datasetName,
  rowCount,
  dataRevision,
  columnHeaderActions,
  onControlsChange,
}) => {
  const theme = useDatasetTheme();
  const datasetModel = useOptionalDatasetModel();
  // Column display options are shared dataset state: seed the grid from what is
  // saved, and persist a change back through the page's model. Both are stable —
  // memoized on the model — so they do not rebuild the grid's view model, which
  // would reset sorts, selection and expansion. The picker mounts this grid
  // without a model, where the getter yields nothing and no sink persists.
  const getInitialColumnDisplayOptions = React.useCallback(
    () => datasetModel?.savedColumnDisplayOptions() ?? [],
    [datasetModel]
  );
  const onColumnDisplayOptionsChange = React.useCallback(
    (
      columns: {
        columnName: string;
        displayOptions: DataGridColumnDisplayOptions;
      }[]
    ) => {
      if (datasetModel === null) {
        return;
      }
      void datasetModel.persistColumnDisplayOptions(columns);
    },
    [datasetModel]
  );
  const {
    rowSource,
    rowDetail,
    status,
    viewModel,
    columnStats,
    customSelectSql,
    search,
    queryError,
    driftedRowIds,
    requestColumnStats,
    applyCellEdit,
    saveEdits,
    runQuery,
    refresh,
    downloadCsv,
    openRowIdCursor,
    aggregate,
    addRow,
    addColumn,
    renameColumn,
    deleteColumn,
    deleteRows,
  } = useDatasetPreviewGridSource(adapter, composedLayers, {
    layerList: layers,
    readOnly,
    getInitialColumnDisplayOptions,
    onColumnDisplayOptionsChange,
  });
  const statusValue = useWatchableValue(status);
  const driftedRowCount = useWatchableValue(driftedRowIds).size;
  const runPreviewQueryAction = useAction(RunDatasetPreviewQueryAction);
  const saveDatasetAction = useAction(SaveDatasetAction);

  // A column's "+ N more" categorical row asks the header to expand; we open the
  // full breakdown in the app modal stack through an action. The grid stays
  // generic — the handler is injected into the header component here rather than
  // threaded through DataGrid props. Read through a ref so `HeaderComponent`
  // keeps a stable identity (a new component type would remount every header).
  const breakdownAction = useAction(ViewCategoryBreakdownAction);
  const breakdownExecuteRef = React.useRef(breakdownAction.execute);
  React.useEffect(() => {
    breakdownExecuteRef.current = breakdownAction.execute;
  });
  const HeaderComponent = React.useCallback(
    (props: DataGridHeaderComponentProps<DatasetPreviewRow>) => (
      <DataGridHeader
        {...props}
        onExpandCategory={(request) =>
          void breakdownExecuteRef.current(request)
        }
      />
    ),
    []
  );

  /**
   * Picks up rows from an artifact published while this grid was open.
   *
   * A re-query rather than a rebuild: the columns are the same, so everything
   * the user arranged — sorts, filters, selection, expansion, scroll — is still
   * meaningful and stays. The revision this grid mounted on is skipped, since
   * the source's own first load already read it.
   */
  const queriedRevision = React.useRef(dataRevision);
  useEffect(() => {
    if (queriedRevision.current === dataRevision) {
      return;
    }
    queriedRevision.current = dataRevision;
    refresh();
  }, [dataRevision, refresh]);

  useEffect(() => {
    onStatusChange(statusValue);
  }, [onStatusChange, statusValue]);

  /**
   * Lends the page's model the one thing it cannot do itself: run a query
   * against this grid's DuckDB session. That is what lets Ctrl+Enter work from
   * anywhere on the page, and the command palette offer the run at all.
   */
  useEffect(() => {
    if (datasetModel === null || !adapter.queryable) {
      return;
    }
    return datasetModel.registerPreviewQueryRunner(runQuery);
  }, [adapter.queryable, datasetModel, runQuery]);

  /**
   * Every run the footer starts — the button, Ctrl+Enter in the box — goes
   * through the action, so a run from the keyboard, the palette and the button
   * are one path and all of them adopt the query the user is looking at.
   *
   * The row picker mounts this grid outside any dataset model, where there is
   * nothing for the action to target and no page state to adopt the query into;
   * there the local runner is the whole story.
   */
  const submitQuery = React.useCallback(
    (query: string) => {
      if (datasetModel === null) {
        void runQuery(query);
        return;
      }
      void runPreviewQueryAction.execute({ query });
    },
    [datasetModel, runPreviewQueryAction, runQuery]
  );

  /**
   * Keeping the applied statement on the dataset, so the next person to open it
   * lands on it. Offered only where there is a dataset to keep it on and the
   * viewer may write to it; elsewhere the query stays this session's own.
   */
  const queryDirty = useMemo(
    () =>
      datasetModel === null || readOnly
        ? Watchable.fromValue(false)
        : datasetModel.hasUnsavedChangesWatchable,
    [datasetModel, readOnly]
  );
  const saveQuery = React.useCallback(() => {
    void saveDatasetAction.execute({});
  }, [saveDatasetAction]);

  /**
   * Drops the picked rows, then unpicks them: a selection naming rows that no
   * longer exist would keep the "Delete N rows" button on the bar with nothing
   * behind it, and the next Delete would send ids the dataset has never heard
   * of. Cleared only on success, so a failed delete leaves the user's selection
   * intact to try again.
   */
  const handleDeleteRows = React.useCallback(
    async (rowIds: readonly DataGridRowId[]) => {
      await deleteRows(rowIds);
      viewModel.clearSelection();
    },
    [deleteRows, viewModel]
  );

  /**
   * What a column's header control gets to act on: the rows the grid is
   * showing, and the DuckDB session behind them.
   *
   * Memoized so the control re-renders only when something it reads has
   * actually moved — every page query touches `shownRows`, and a control with
   * an open menu is a common state to be in while one lands.
   */
  const shownRows = useWatchableValue(rowSource.totalRows);
  const hasFilters = useWatchableValue(viewModel.filters).length > 0;
  const headerContext = React.useMemo<DatasetPreviewHeaderContext>(
    () => ({ shownRows, rowCount, hasFilters, openRowIdCursor, aggregate }),
    [aggregate, hasFilters, openRowIdCursor, rowCount, shownRows]
  );

  /**
   * Everything the top bar and the footer need, held in a ref so that neither
   * component's *identity* depends on it.
   *
   * Both are handed to `DataGrid` as component types and rendered as
   * `<ResolvedTopBarComponent />` / `<ResolvedFooterComponent />`. React
   * compares element types by reference, so a new function is a different
   * component: it unmounts the old subtree and mounts a fresh one, discarding
   * every piece of local state inside it. That is not a re-render, and no
   * amount of memoizing the props below it helps.
   *
   * The cost was ordinary use breaking. `rowCount` and `driftedRowCount` move
   * whenever a run lands, so the top bar was being remounted continuously —
   * closing any menu the user had open. The footer holds the ad-hoc query box,
   * whose draft is local state, so a remount silently threw away whatever had
   * been typed but not yet run.
   *
   * Both callbacks below therefore take an empty dependency list and read
   * through this, refreshed on every render. `DataGrid` is not memoized, so any
   * render of this component re-renders it and the values read here are current.
   */
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  useDatasetSearchHotkey(searchInputRef);

  /**
   * The dataset's own account of an empty body.
   *
   * Only the search is ours to explain — a column filter is the grid's, and it
   * already says so better than we could, so that case falls straight through
   * to the default.
   *
   * Read at render rather than subscribed to: `renderEmpty` is called from the
   * grid's own render, which is already subscribed to the columns and to the
   * rows that emptied, so a snapshot here is current by construction.
   */
  const renderEmpty = React.useCallback(
    (input: DataGridEmptyStateProps) => {
      const term = search.snapshot().trim();
      // Only the search is ours to explain — a column filter is the grid's, and
      // it already says so better than we could.
      return term === '' ? (
        <DataGridEmptyState {...input} />
      ) : (
        <DatasetSearchEmptyState
          term={term}
          onClearSearch={() => search.set('')}
        />
      );
    },
    [search]
  );

  const chrome = {
    editable: adapter.editable,
    canImportCsv,
    addRow,
    addColumn,
    onImportCsv,
    driftedRowCount,
    refresh,
    rowCount,
    downloadCsv,
    datasetName,
    deleteRows: handleDeleteRows,
    clearSelection: () => viewModel.clearSelection(),
    search,
    searchInputRef,
    customSelectSql,
    queryError,
    queryable: adapter.queryable,
    queryColumns: adapter.columns,
    submitQuery,
    queryDirty,
    saveQuery,
  };
  const chromeRef = React.useRef(chrome);
  chromeRef.current = chrome;

  /**
   * Hand the page the two controls that are the grid's to perform but the
   * page's to offer.
   *
   * Rebuilt only when the view model is, and reading everything else through
   * `chromeRef`, so the page is not handed a new object on every render — it
   * stores this in state, and an unstable identity would loop.
   */
  const controls = React.useMemo<DatasetPreviewGridControls>(
    () => ({
      rowHeight: viewModel.rowHeightPreset,
      setRowHeight: (next) => viewModel.setRowHeight(next),
      downloadCsv: () =>
        void chromeRef.current.downloadCsv(chromeRef.current.datasetName),
    }),
    [viewModel]
  );
  React.useEffect(() => {
    onControlsChange?.(controls);
    // Cleared on unmount so the page's menu drops these with the grid rather
    // than keeping entries that would act on a view model nobody is showing.
    return () => onControlsChange?.(null);
  }, [controls, onControlsChange]);

  const TopBarComponent = React.useCallback(
    (props: DataGridTopBarProps<DatasetPreviewRow>) => {
      const chrome = chromeRef.current;
      return (
        <DatasetPreviewTopBar
          {...props}
          search={chrome.search}
          searchInputRef={chrome.searchInputRef}
          editable={chrome.editable}
          canImportCsv={chrome.canImportCsv}
          onAddRow={chrome.addRow}
          onAddColumn={chrome.addColumn}
          onImportCsv={chrome.onImportCsv}
          driftedRowCount={chrome.driftedRowCount}
          onRefresh={chrome.refresh}
          rowCount={chrome.rowCount}
          onDeleteRows={chrome.deleteRows}
          onClearSelection={chrome.clearSelection}
        />
      );
    },
    []
  );

  /**
   * The caller's control, rendered in the header of the column it belongs to.
   *
   * Only that column gets it: every other header asks and is told there is
   * nothing to draw.
   */
  const renderColumnHeaderActions = React.useCallback(
    (context: DataGridHeaderContext<DatasetPreviewRow>) =>
      columnHeaderActions === undefined ||
      context.column.id !== columnHeaderActions.columnId
        ? null
        : columnHeaderActions.render(headerContext),
    [columnHeaderActions, headerContext]
  );

  /**
   * A "Delete column" entry appended to each header's dropdown, below the
   * built-in sort/pin/hide items.
   *
   * Only real dataset columns are offered: internal bookkeeping columns and the
   * layers' projected columns (e.g. the automations summary) are not part of the
   * dataset schema, so there is no `delete_column` for them to run. Read-only
   * surfaces get nothing — they never write.
   */
  const confirmDeleteColumn = useConfirmDeleteColumn();
  const promptRenameColumn = useRenameColumn();
  const renderColumnMenuItems = React.useCallback(
    (context: DataGridHeaderContext<DatasetPreviewRow>) => {
      const columnName = context.column.id;
      if (
        readOnly ||
        isInternalDatasetColumn(columnName) ||
        !adapter.columns.some((column) => column.name === columnName)
      ) {
        return null;
      }
      const label =
        typeof context.column.header === 'string'
          ? context.column.header
          : columnName;
      return (
        <>
          <DropdownMenuItem
            onSelect={() => {
              void (async () => {
                // Every column the dataset has, including the internal and
                // hidden ones: a name is taken whether or not the grid is
                // currently drawing the column that holds it.
                const takenNames = new Set(
                  adapter.columns.map((column) => column.name)
                );
                const nextName = await promptRenameColumn({
                  columnLabel: label,
                  takenNames,
                });
                if (nextName === null) {
                  return;
                }
                await renameColumn({
                  oldName: columnName,
                  newName: nextName,
                });
                // The saved formatting is keyed by column name too, and the
                // rebuild after a rename skips anything naming a column the
                // schema no longer has — so a renamed column would come back
                // with its format, decimals and alignment reset. The entry
                // under the old name is left behind rather than deleted: the
                // sink upserts, and a setting for a column that no longer
                // exists is already ignored on the way in.
                const saved = datasetModel?.savedColumnDisplayOptions() ?? [];
                if (saved.some((entry) => entry.columnName === columnName)) {
                  void datasetModel?.persistColumnDisplayOptions(
                    saved.map((entry) =>
                      entry.columnName === columnName
                        ? { ...entry, columnName: nextName }
                        : entry
                    )
                  );
                }
              })();
            }}
          >
            <Icon icon="pencil" size="small" />
            Rename column…
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            onSelect={() => {
              void (async () => {
                if (await confirmDeleteColumn(label)) {
                  await deleteColumn({ name: columnName });
                }
              })();
            }}
          >
            <Icon icon="trash" size="small" />
            Delete column
          </DropdownMenuItem>
        </>
      );
    },
    [
      adapter.columns,
      confirmDeleteColumn,
      datasetModel,
      deleteColumn,
      promptRenameColumn,
      readOnly,
      renameColumn,
    ]
  );

  // Stable for the life of the mount, for the reason set out at `chromeRef`:
  // this is a component type, and a new one throws away the query box's draft.
  const FooterComponent = React.useCallback(
    (props: DataGridFooterProps<DatasetPreviewRow>) => {
      const chrome = chromeRef.current;
      return (
        <DatasetPreviewFooter
          {...props}
          customSelectSql={chrome.customSelectSql}
          queryError={chrome.queryError}
          queryable={chrome.queryable}
          queryColumns={chrome.queryColumns}
          onRunQuery={chrome.submitQuery}
          queryDirty={chrome.queryDirty}
          onSaveQuery={chrome.saveQuery}
        />
      );
    },
    []
  );

  // The applied term, not the draft: the rows on screen answer to what the grid
  // last ran, so marking what is still being typed would point at matches the
  // page has not been filtered by yet.
  const appliedSearch = useWatchableValue(search);

  return (
    <div className="min-h-0 flex-1">
      <DataGridHighlightContext.Provider value={appliedSearch}>
        <DataGrid
          // The grid sits flush inside the card, so it takes the card's own
          // radius rather than the DataGrid default rounding inside it.
          className={cn(theme.surface, theme.surfaceRadius)}
          rowSource={rowSource}
          getRowId={adapter.getRowId}
          viewModel={viewModel}
          columnStatsSource={columnStats}
          onHeaderStatsVisible={requestColumnStats}
          // Lets a user open any cell full-size to read a long value, and edit it
          // there when the cell is editable.
          enableCellDetail
          HeaderComponent={HeaderComponent}
          renderEmpty={renderEmpty}
          renderColumnHeaderActions={renderColumnHeaderActions}
          renderColumnMenuItems={renderColumnMenuItems}
          {...(!readOnly && {
            onCellEdit: applyCellEdit,
            onSaveEdits: saveEdits,
            // Edits persist the moment they are committed rather than collecting
            // behind a Save/Revert banner.
            autoSaveEdits: true,
          })}
          TopBarComponent={TopBarComponent}
          FooterComponent={FooterComponent}
          {...(rowDetail !== null && { renderRowDetail: rowDetail.render })}
        />
      </DataGridHighlightContext.Provider>
    </div>
  );
};

/**
 * What a caller's header control can read and act on.
 *
 * Passed rather than left to the caller to rebuild, because both halves belong
 * to the grid: the filters are its view-model state, and the row ids come from
 * its DuckDB session. A control that re-derived either would drift from what the
 * user is actually looking at.
 */
export type DatasetPreviewHeaderContext = {
  /** Rows the grid is showing, after filters. Null until the first query lands. */
  shownRows: number | null;
  /**
   * The artifact's rows and the dataset's true total. They differ when the
   * artifact is a sample — a large warehouse dataset — and then the cursor and
   * aggregate below only see the sample.
   */
  rowCount: DatasetRowCountOutput | null;
  hasFilters: boolean;
  /** The caller owns the cursor it opens, and must close it. */
  openRowIdCursor: (input: {
    scope: 'all' | 'filtered';
    extraWhereSql?: string;
  }) => Promise<DatasetRowIdCursor>;
  aggregate: (input: {
    scope: 'all' | 'filtered';
    selectSql: string;
  }) => Promise<Record<string, unknown>>;
};

/**
 * A caller's control for one column's header, right of its name.
 *
 * The column is named rather than inferred because the control belongs to what
 * that column *is* — the automations column's Run menu acts on the runs it
 * reports — while the grid only knows it as one more column of the source.
 */
export type DatasetPreviewColumnHeaderActions = {
  columnId: string;
  render: (context: DatasetPreviewHeaderContext) => React.ReactNode;
};

/**
 * The grid controls that belong in the page's menu rather than in the grid.
 *
 * Row height and the CSV export act on the grid but are not writes to the
 * dataset, and they used to sit behind a cog in the grid's own toolbar — a
 * second menu a few pixels below the page's, holding two entries that were
 * already in it under different names. The page owns one menu now, so the grid
 * hands out the two controls that were only reachable through the cog.
 *
 * `rowHeight` is a watchable, not a value: the menu renders outside the grid's
 * render, so it has to carry its own subscription or the chosen option would
 * never light up.
 */
export type DatasetPreviewGridControls = {
  rowHeight: ReadonlyWatchable<DataGridRowHeight>;
  setRowHeight: (rowHeight: DataGridRowHeight) => void;
  downloadCsv: () => void;
};

/**
 * How many times the grid re-resolves the parquet URL and re-reads before it
 * gives up.
 *
 * The read can fail for a reason that is already gone by the next attempt: a
 * signed URL that 403s once its signature expires, or the artifact being pruned
 * out from under a grid a few generations behind on a dataset that is being
 * actively written. The materialization effect has no other trigger to recover
 * from — it re-runs only when the artifact or schema changes — so a single
 * failure would otherwise latch the grid on "Could not load parquet preview"
 * until the next write or a reload.
 */
const DATASET_PREVIEW_MATERIALIZATION_ATTEMPTS = 4;

/** First backoff between those attempts; doubled each retry. */
const DATASET_PREVIEW_RETRY_BASE_MS = 250;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Whether two schemas describe the same columns, in the same order.
 *
 * Compared by value because the array's *identity* is load-bearing: it is what
 * the layer stack, the adapter and ultimately the grid's view model are
 * memoized on, so an equal-but-new array resets the grid. Name and type are the
 * whole schema — a short list of two-field objects — so this is cheap and exact.
 */
const sameColumnSchema = (
  left: readonly { name: string; type: string | null }[],
  right: readonly { name: string; type: string | null }[]
): boolean =>
  left.length === right.length &&
  left.every(
    (column, index) =>
      column.name === right[index]?.name && column.type === right[index]?.type
  );

export const DatasetPreviewGrid: React.FC<{
  artifact: DatasetArtifactOutput | null;
  metadata: DatasetArtifactMetadataOutput | null;
  preview: DatasetOutput['preview'];
  datasetType: DatasetOutput['type'];
  /** Names an exported CSV. Defaults to something neutral for embedded grids. */
  datasetName?: string;
  /**
   * Laid over the dataset's own columns, base-first. Each must be stable
   * across renders — the view model is rebuilt whenever one's identity
   * changes, which resets sorts, selection and expansion.
   */
  layers?: readonly DatasetPreviewLayer[];
  /**
   * Renders the dataset as a viewer: no cell editing, no structural edits and
   * no writes of any kind back to the dataset.
   */
  readOnly?: boolean;
  /**
   * Controls for one column's header.
   *
   * A render prop rather than a node, because what these controls need — the
   * filtered row count, and the row ids behind it — only exists inside the grid.
   */
  columnHeaderActions?: DatasetPreviewColumnHeaderActions;
  /**
   * Publishes the grid-level controls the page renders in its own menu, and
   * null on unmount so the page drops the entries with the grid. Stable
   * identity expected — a `useState` setter is what this is for.
   */
  onControlsChange?: (controls: DatasetPreviewGridControls | null) => void;
}> = ({
  artifact,
  metadata,
  preview,
  datasetType,
  datasetName = 'dataset',
  layers: callerLayers,
  readOnly = false,
  columnHeaderActions,
  onControlsChange,
}) => {
  const theme = useDatasetTheme();
  const canImportCsv = datasetType === 'manual' && !readOnly;
  // Optional: the picker renders this grid outside the dataset page's model
  // scope. There, a query is a way to find rows rather than an edit to the
  // dataset's saved preview query, so it stays local instead of persisting.
  const datasetModel = useOptionalDatasetModel();
  const queryable = preview.queryable;
  const handleQueryChanged = React.useCallback(
    (query: string) => {
      if (readOnly) {
        return;
      }
      datasetModel?.setPreviewQuery(query);
    },
    [datasetModel, readOnly]
  );
  /**
   * A new column belongs to the schema every read is built from, so it is
   * appended here rather than patched into the grid: the adapter is rebuilt and
   * the grid picks the column up for reads, filters and stats alike.
   */
  const handleColumnAdded = React.useCallback(
    (column: { name: string; type: string }) =>
      setPreviewColumns((current) =>
        current.some((candidate) => candidate.name === column.name)
          ? current
          : [...current, column]
      ),
    []
  );
  /**
   * A renamed column keeps its place and its type; only the name every read
   * addresses it by moves. Patched in position rather than removed and
   * re-appended, so a rename does not send the column to the end of the grid.
   */
  const handleColumnRenamed = React.useCallback(
    (change: { oldName: string; newName: string }) =>
      setPreviewColumns((current) =>
        current.map((candidate) =>
          candidate.name === change.oldName
            ? { ...candidate, name: change.newName }
            : candidate
        )
      ),
    []
  );
  /**
   * A dropped column leaves the schema every read is built from, so it is
   * removed here rather than patched into the grid: the adapter is rebuilt and
   * the grid stops reading, filtering and gathering stats for it.
   */
  const handleColumnRemoved = React.useCallback(
    (column: { name: string }) =>
      setPreviewColumns((current) =>
        current.filter((candidate) => candidate.name !== column.name)
      ),
    []
  );
  const client = useTenantedClient();
  // Read through a ref rather than listed as a dependency of the
  // materialization effect below, which is the only thing that calls it.
  //
  // WorkOS re-mints this callback whenever it refreshes the session — on its
  // own schedule, a few times an hour, with nothing about the dataset having
  // changed. As a dependency that re-ran the whole materialization: re-download
  // the parquet, replay the operation log, and hand back a fresh column array,
  // which rebuilds the adapter and with it the entire grid view model. The
  // symptom was the grid resetting itself at random intervals — sorts, filters,
  // selection, scroll and expansion all gone — for no reason a user could see
  // or reproduce.
  const getAccessToken = useGetAccessToken();
  const getAccessTokenRef = React.useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const datasetId = artifact?.datasetId ?? null;
  const artifactId = artifact?.id ?? null;
  // Through a ref so writes see the current artifact without the adapter — and
  // with it the whole view model — being rebuilt every time compaction
  // publishes one.
  const baseArtifactIdRef = React.useRef<string | null>(artifactId);
  baseArtifactIdRef.current = artifactId;
  const getBaseArtifactId = React.useCallback(
    () => baseArtifactIdRef.current,
    []
  );
  /**
   * Memoized on the schema's *content*, not the array it arrived in.
   *
   * Every refetch of the dataset mints a fresh metadata object, so keying on the
   * reference made an unrelated change — renaming the dataset — look like a new
   * schema. That re-ran the materialization effect below, which re-downloads the
   * parquet, replays the operation log and rebuilds the view model, resetting
   * sorts, filters, selection and scroll. The schema is a short list of
   * `{ name, type }`, so comparing it by value is cheap and exact.
   */
  const columnsSignature = JSON.stringify(metadata?.schema.columns ?? []);
  const columns = useMemo(
    () => metadata?.schema.columns ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsSignature]
  );

  const handleImportCsv = useImportDatasetCsv(datasetId);
  /**
   * Survives re-materialization, which is the whole point: the preview table
   * is rebuilt from the parquet whenever the artifact changes or an effect
   * re-runs, and an edit that lived only in that table went with it.
   *
   * Keyed on the dataset rather than the artifact — a new artifact means
   * compaction has folded the *saved* edits in, and anything still pending is
   * unsaved and still has to be replayed.
   */
  const pendingEdits = useMemo(
    () => new DatasetPendingEdits(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datasetId]
  );

  const [registeredTableName, setRegisteredTableName] = useState<string | null>(
    null
  );
  const [dataRevision, setDataRevision] = useState(0);
  const [previewColumns, setPreviewColumns] = useState(columns);

  /**
   * The DuckDB table this artifact is previewed through.
   *
   * Held here rather than built inside the materialization effect so the live
   * operation sync can write to the same object — and so both go through the
   * same per-table write lock, which is what keeps an increment from landing in
   * the middle of a rebuild's replay.
   */
  // A writeback the replay had to drop because a source reseed left its column
  // off the artifact. Surfaced as one event per materialization — the op type
  // and column names only, never the value, which can be customer data — so the
  // otherwise-silent skip that the binder-crash guard produces stays observable.
  const analytics = useAnalytics();
  const reportSkippedOperations = useCallback<DatasetOperationSkipReporter>(
    (skipped) => {
      analytics.track('Dataset Preview Operation Skipped', {
        reason: 'missing-preview-column',
        datasetId,
        operations: skipped.map((entry) => entry.operation),
        columns: skipped.map((entry) => entry.columnName),
      });
    },
    [analytics, datasetId]
  );

  // Keyed on the dataset, so a compaction publishing a new artifact refills the
  // same table instead of creating another one — see `DatasetPreviewTable`.
  const previewTable = useMemo(
    () =>
      datasetId === null
        ? null
        : new DatasetPreviewTable(
            datasetId,
            undefined,
            reportSkippedOperations
          ),
    [datasetId, reportSkippedOperations]
  );

  /**
   * Writes that reach the dataset from somewhere other than this grid.
   *
   * Reads `previewColumns` rather than the artifact's own schema because that is
   * what the table actually holds once its initial replay has run, and an
   * inserted row has to be filled out against the columns the grid is showing.
   */
  const {
    layer: liveOperationsLayer,
    schemaVersion,
    markApplied,
  } = useLiveDatasetOperations({
    datasetId,
    artifactId,
    // Withheld until the name the materialization registered is *this*
    // artifact's. On a compaction the two disagree for as long as the new
    // parquet takes to read, and applying an UPDATE to a table that has not
    // been created yet fails outright.
    table:
      previewTable !== null && registeredTableName === previewTable.name
        ? previewTable
        : null,
    columns: previewColumns,
    rowIdColumn: datasetRowIdColumn(previewColumns),
    client,
  });
  const [previewStatus, setPreviewStatus] =
    useState<DatasetPreviewStatus>('idle');
  // Bumped to re-run the materialization effect on demand — the manual "Retry"
  // the error state offers. Everything else that re-runs the effect is a real
  // change to what it reads; this is the one trigger that is not.
  const [retryNonce, setRetryNonce] = useState(0);
  // Artifact ids this grid has already tried to recover from by re-fetching the
  // dataset. A read that fails because its artifact was superseded and pruned
  // out from under the grid recovers by pulling the current one; a read that
  // fails for any other reason would pull the same id and fail again, so each id
  // gets a single recovery before the grid latches and offers a manual retry.
  const recoveredArtifactIdsRef = React.useRef<Set<string>>(new Set());
  // In flight while a warehouse dataset is being re-seeded from the error state,
  // so the button that started it cannot be pressed twice.
  const [repopulating, setRepopulating] = useState(false);
  const [servedRowCount, setServedRowCount] =
    useState<DatasetRowCountOutput | null>(null);

  /**
   * What the artifact already says about itself, which is available on the
   * first paint: the metadata arrives with the dataset, long before there is a
   * parquet to open.
   *
   * The count the header would otherwise show is a `count(*)` in DuckDB, and
   * everything has to happen before it can run — the parquet downloaded, the
   * table registered, the operation log replayed over it. That is seconds on a
   * large dataset, and for all of them the header would be reduced to counting
   * its own skeleton rows.
   */
  const metadataRowCount = useMemo<DatasetRowCountOutput | null>(
    () =>
      metadata === null
        ? null
        : {
            artifactRows: metadata.rowCount,
            totalRows: metadata.sourceRowCount ?? metadata.rowCount,
          },
    [metadata]
  );
  /**
   * The server's answer wins once it lands, and not merely because it is newer.
   * The metadata describes the artifact, so it can only ever report a sample by
   * quoting the count taken when that sample was written; a kind whose rows live
   * upstream answers `totalRows` from the source as it is now. Until then the
   * metadata is a correct answer rather than a placeholder, so there is nothing
   * to hide while the request is in flight.
   */
  const rowCount = servedRowCount ?? metadataRowCount;

  /**
   * How big the dataset actually is, which only the server can say: the browser
   * has the artifact and nothing else, and an artifact carries no record of what
   * it was taken from.
   *
   * Asked here rather than by the page so every grid gets it — the trigger row
   * picker renders this same component outside the dataset page entirely, and a
   * sample is no less a sample there.
   *
   * Re-asked on a new artifact, which is exactly when both numbers move: a
   * compaction, a re-upload, or a refreshed pull all land as one.
   */
  useEffect(() => {
    if (datasetId === null || artifactId === null) {
      setServedRowCount(null);
      return;
    }
    let cancelled = false;
    void client.resource.dataset
      .rowCount({ id: datasetId })
      .then((result) => {
        if (!cancelled && result.isOk()) {
          setServedRowCount(result.value);
        }
      })
      .catch(() => {
        // A refusal costs nothing: the metadata already answered, and the
        // header keeps showing what it said rather than emptying out.
      });
    return () => {
      cancelled = true;
    };
  }, [artifactId, client, datasetId]);

  const hasMetadata = metadata !== null;
  useEffect(() => {
    if (
      artifactId === null ||
      datasetId === null ||
      previewTable === null ||
      !hasMetadata ||
      columns.length === 0
    ) {
      setRegisteredTableName(null);
      setPreviewColumns(columns);
      setPreviewStatus('idle');
      return;
    }

    let cancelled = false;
    setPreviewStatus('loading');

    const load = async () => {
      const preview = previewTable;
      const operationsResult = await client.resource.dataset.listOperations({
        id: datasetId,
      });
      if (operationsResult.isErr()) {
        throw new Error('Could not load dataset operations');
      }
      // The high-water mark this replay covers, so a later broadcast can tell
      // a write it has already got from one it has not.
      markApplied(
        operationsResult.value.operations.map(
          (operation) => operation.sequenceNumber
        )
      );
      const operations = operationsResult.value.operations.map(
        (operation) => operation.payload
      );

      // The register-and-read is retried on its own because it is the part that
      // fails transiently: a signed URL 403s once expired, and an artifact is
      // pruned out from under a grid that is behind on an actively-written
      // dataset — both leave the read against a dead URL, and both are gone by
      // the next attempt because a fresh URL is resolved each time. `replace`
      // is what makes that fresh URL take, since `registerFileURL` caches by
      // name and would otherwise keep the dead one. A superseded run
      // (`cancelled`) stops rather than retries — a later run already owns the
      // outcome.
      const shouldRun = () => !cancelled;
      let lastError: unknown;
      for (
        let attempt = 0;
        attempt < DATASET_PREVIEW_MATERIALIZATION_ATTEMPTS;
        attempt++
      ) {
        if (cancelled) {
          return;
        }
        try {
          await preview.register(
            artifactId,
            await resolveDatasetParquetUrl({
              client,
              datasetId,
              artifactId,
              token: await getAccessTokenRef.current(),
            }),
            { replace: attempt > 0 }
          );
          // `columns` is the artifact's own schema, not the post-replay one:
          // the replay resolves the row-id column step by step from what the
          // table looks like at that point, so it has to start where the
          // parquet does. What comes back is the schema it ended up with.
          const loadedColumns = await preview.load(
            {
              artifactId,
              operations,
              columns,
              pendingEdits,
            },
            { shouldRun }
          );
          // Undefined means a later run superseded this one, which has already
          // set — or will set — the state this would have.
          if (loadedColumns !== undefined) {
            // Kept by identity when the schema has not actually changed. This
            // array is what the layer stack and the adapter are memoized on,
            // and the adapter is what the grid's whole view model is memoized
            // on — so handing back an equal-but-new array on a re-run of this
            // effect throws away sorts, filters, selection, scroll and
            // expansion for nothing.
            setPreviewColumns((current) =>
              sameColumnSchema(current, loadedColumns)
                ? current
                : [...loadedColumns]
            );
            setRegisteredTableName(preview.name);
            // The table now holds a different artifact's rows under the same
            // name, which nothing downstream can infer from the adapter — it
            // did not change, deliberately.
            setDataRevision((current) => current + 1);
          }
          return;
        } catch (error) {
          lastError = error;
          if (
            cancelled ||
            attempt === DATASET_PREVIEW_MATERIALIZATION_ATTEMPTS - 1
          ) {
            break;
          }
          // eslint-disable-next-line no-console
          console.warn(
            `[dataset-preview] materialization attempt ${
              attempt + 1
            } failed; retrying`,
            error
          );
          await sleep(DATASET_PREVIEW_RETRY_BASE_MS * 2 ** attempt);
        }
      }
      throw lastError;
    };

    const latch = () => {
      if (!cancelled) {
        setPreviewColumns(columns);
        setPreviewStatus('error');
        setRegisteredTableName(null);
      }
    };

    void load().catch((error: unknown) => {
      // Logged, not just rendered. The grid's error state says "Could not load
      // parquet preview" and nothing else, so a failure anywhere in the
      // download/replay chain — a bad URL, a DuckDB statement the operation
      // log generated, a schema the parquet does not have — is otherwise
      // indistinguishable from every other one, with nothing to debug from.
      // eslint-disable-next-line no-console
      console.error('[dataset-preview] materialization failed', error);
      if (cancelled) {
        return;
      }
      // The artifact this grid holds can be superseded and pruned out from under
      // it — a workflow writing back, a reseed, another tab's edit all publish a
      // new one — and a read against the old bytes then fails however many times
      // the loop above retries it, because they are gone. Recovery is not one
      // more retry but a different question: re-fetch the dataset and see whether
      // the current artifact has moved on. If it has, the new one arrives as a
      // prop and re-runs this effect, so the status stays on 'loading' and hands
      // off rather than flashing an error; if it has not, this is a genuine
      // failure and the grid latches with a retry. One recovery per artifact id,
      // so a truly broken artifact cannot spin the refetch.
      const failedArtifactId = artifactId;
      if (
        datasetModel === null ||
        failedArtifactId === null ||
        recoveredArtifactIdsRef.current.has(failedArtifactId)
      ) {
        latch();
        return;
      }
      recoveredArtifactIdsRef.current.add(failedArtifactId);
      void datasetModel
        .reloadResource()
        .then(() => {
          const currentArtifactId =
            datasetModel.datasetResourceWatchable.snapshot().resource?.dataset
              .artifacts?.[0]?.id ?? null;
          // A moved artifact re-runs this effect off its new prop, which owns the
          // outcome from here; latching only when it did not move avoids racing
          // that run to an error state it is about to clear.
          if (currentArtifactId === failedArtifactId) {
            latch();
          }
        })
        .catch(latch);
    });

    return () => {
      cancelled = true;
    };
    // Keyed on the artifact's *identity*, not the object: a refetch hands back a
    // new object for the same artifact, and re-materializing on that would throw
    // away the grid's state every time anything about the dataset changed.
    //
    // `pendingEdits` is stable per dataset, so listing it does not re-run this
    // — but leaving it out would hide a real re-materialization if that ever
    // changed.
    // `schemaVersion` changes only when an operation arrived that no patch can
    // express — a column added, renamed or dropped — because the column list is
    // what this effect produces and what everything downstream is built from.
    // Everything else that reaches the dataset from elsewhere is patched into
    // the loaded rows instead, which is the whole point: rebuilding here costs
    // the user their sorts, filters, selection and scroll position.
    // `retryNonce` is the manual retry: bumping it re-runs this from the top with
    // a freshly resolved URL. `datasetModel` is stable from context and only
    // read on the recovery path; listing it keeps that path honest.
  }, [
    artifactId,
    client,
    columns,
    datasetId,
    hasMetadata,
    pendingEdits,
    previewTable,
    schemaVersion,
    markApplied,
    retryNonce,
    datasetModel,
  ]);

  /**
   * The manual way out of the error state. Clears the one-shot recovery guard for
   * the current artifact so a retry can re-fetch the dataset again — the user
   * asking is reason enough to try the whole recovery once more — and re-runs the
   * materialization from the top with a freshly resolved URL.
   */
  const handleRetryPreview = React.useCallback(() => {
    if (artifactId !== null) {
      recoveredArtifactIdsRef.current.delete(artifactId);
    }
    setPreviewStatus('loading');
    setRetryNonce((current) => current + 1);
  }, [artifactId]);

  /**
   * The warehouse dataset's honest way out when a retry cannot help — the
   * artifact's bytes are genuinely gone (a bucket lifecycle rule, a restore, an
   * environment seeded from another's database), so there is nothing to re-read.
   *
   * A warehouse dataset's rows live upstream, so it can be rebuilt from the
   * SELECT it was seeded with — which is exactly what a re-seed does, writing a
   * fresh artifact and file. Only offered when that source is known (`sourceQuery`
   * on the resource) and there is a model to read it from; a CSV or manual
   * dataset has no source to regenerate from, so its artifact is the only copy
   * and this is deliberately absent. Mirrors {@link WarehouseReseedForm}: reseed,
   * then flip the page to its import progress without waiting for a broadcast.
   */
  const warehouseSourceQuery = isWarehouseDataset(datasetType)
    ? datasetModel?.datasetResourceWatchable.snapshot().resource?.dataset
        .sourceQuery ?? null
    : null;
  const canRepopulateFromSource =
    !readOnly &&
    datasetId !== null &&
    warehouseSourceQuery !== null &&
    warehouseSourceQuery.trim() !== '';
  const handleRepopulateFromSource = React.useCallback(async () => {
    if (
      datasetId === null ||
      warehouseSourceQuery === null ||
      warehouseSourceQuery.trim() === '' ||
      repopulating
    ) {
      return;
    }
    setRepopulating(true);
    try {
      const result = await client.resource.dataset.reseedWarehouse({
        id: datasetId,
        sourceQuery: warehouseSourceQuery.trim(),
      });
      if (result.isErr()) {
        toast.error(result.error.err.message ?? 'Could not start the query.');
        return;
      }
      // Flip the page to its import progress immediately, which also clears this
      // error state — the same handoff the reseed form makes.
      emitLocalDatasetImport(datasetId, { phase: 'staged' });
    } finally {
      // Always clears the in-flight flag, so a thrown request (a network error
      // rather than an `isErr` result) cannot leave the button stuck disabled.
      setRepopulating(false);
    }
  }, [client, datasetId, repopulating, warehouseSourceQuery]);

  /**
   * The full stack, base-first: live operations, the caller's data layers, then
   * row selection.
   *
   * Live operations first because they are the dataset's own rows — everything
   * else is laid over them. It contributes no columns and no SQL, only the
   * subscription that lets a write from elsewhere reach rows already on screen.
   */
  const layers = React.useMemo<readonly DatasetPreviewLayer[]>(
    () => [liveOperationsLayer, ...(callerLayers ?? [])],
    [callerLayers, liveOperationsLayer]
  );
  const composedLayers = React.useMemo(
    () =>
      new DuckDbLayerStack(layers, {
        rowIdColumn: datasetRowIdColumn(previewColumns),
      }),
    [layers, previewColumns]
  );
  /**
   * What the loading skeleton reserves space for.
   *
   * The stack knows its columns before the parquet does, so the skeleton can
   * show them from the first paint rather than having them appear later and
   * shift every dataset column sideways. Safe to build from a null grid: only
   * the column metadata is read here, never `renderCell`.
   */
  const reservedColumns = React.useMemo(
    () =>
      composedLayers.present({
        gridHolder: { current: null },
        rowsHolder: { current: [] },
        getRowId: (row) => row.__coteraRowId,
      }).columns,
    [composedLayers]
  );

  const previewAdapter = React.useMemo<DatasetPreviewGridAdapter | null>(() => {
    if (
      datasetId === null ||
      registeredTableName === null ||
      previewTable === null
    ) {
      return null;
    }
    return createDuckDbDatasetPreviewGridAdapter({
      // The table, not the artifact. A compaction refills the same table, and
      // keying this on the artifact made every one of them a new adapter — and
      // a new adapter is a new view model, so sorts, filters, selection and
      // scroll went with it.
      key: registeredTableName,
      columns: previewColumns,
      tableName: registeredTableName,
      datasetId,
      getBaseArtifactId,
      rowIdColumn: datasetRowIdColumn(previewColumns),
      queryable,
      // A warehouse dataset's rows come from its source query and a refresh
      // rebuilds them, so the grid does not edit them — workflows write back
      // instead. Only the adapter's writes are off; menus, display options and
      // repopulate-from-source still follow `readOnly`.
      readOnly: readOnly || isWarehouseDataset(datasetType),
      // Snapshotted rather than subscribed: the applied query changes as the
      // user runs one, and rebuilding the adapter would reload the whole grid.
      // No model in scope (the picker) means no saved query to start from, so
      // it opens on the plain select the model itself would have defaulted to.
      initialQuery:
        datasetModel?.previewQueryWatchable.snapshot() ?? DEFAULT_DATASET_QUERY,
      onQueryChanged: handleQueryChanged,
      onColumnAdded: handleColumnAdded,
      onColumnRenamed: handleColumnRenamed,
      onColumnRemoved: handleColumnRemoved,
      layers: composedLayers,
      pendingEdits,
      previewTable,
      markApplied,
      client,
    });
    // Deliberately without `artifactId`: which artifact filled the table is a
    // property of its contents, reported through `dataRevision`, and not of the
    // reader built over it.
  }, [
    client,
    composedLayers,
    datasetId,
    datasetModel,
    getBaseArtifactId,
    handleColumnAdded,
    handleColumnRemoved,
    handleColumnRenamed,
    handleQueryChanged,
    markApplied,
    pendingEdits,
    previewColumns,
    previewTable,
    datasetType,
    queryable,
    readOnly,
    registeredTableName,
  ]);

  if (metadata === null) {
    return (
      <div
        className={cn(
          'border border-dashed p-8 text-center',
          theme.surfaceRadius,
          theme.surfaceBorder,
          theme.surface
        )}
      >
        <Database className="h-10 w-10 mx-auto text-muted-foreground/50" />
        <Text.Title type="title" className="mt-4">
          No dataset preview yet
        </Text.Title>
        <Text.P className="text-muted-foreground mt-2 max-w-xl mx-auto">
          Upload processing has not produced a parquet artifact with metadata
          yet.
        </Text.P>
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div
        className={cn(
          'border p-6',
          theme.surfaceRadius,
          theme.surfaceBorder,
          theme.surface
        )}
      >
        <Text.Title type="title">Dataset preview</Text.Title>
        <Text.P className="text-muted-foreground mt-2">
          No columns were detected for this dataset.
        </Text.P>
      </div>
    );
  }

  // A dataset with nothing but its internal row id has no grid worth showing:
  // it is brand new, and what it needs is a column or a CSV.
  if (
    previewStatus !== 'error' &&
    previewAdapter !== null &&
    previewColumns.every((column) => isInternalDatasetColumn(column.name))
  ) {
    // A viewer has nothing to offer here: the ways out of the empty state are
    // all edits.
    return readOnly ? (
      <div
        className={cn(
          'border p-6 text-center',
          theme.surfaceRadius,
          theme.surfaceBorder,
          theme.surface
        )}
      >
        <Text.P className="text-muted-foreground">
          This dataset has no columns yet.
        </Text.P>
      </div>
    ) : (
      <DatasetEmptyState
        canImportCsv={canImportCsv}
        onAddColumn={previewAdapter.addColumn}
        onImportCsv={handleImportCsv}
      />
    );
  }

  return (
    // The grid carries its own top bar and footer, so the surrounding card only
    // needs to host it — but on a white page it needs an edge of its own, or it
    // has nothing to sit against. A flex column so the grid can claim whatever
    // height the page hands the card, rather than the card having to name one.
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-hidden',
        theme.gridEdges,
        theme.surfaceRadius,
        theme.surfaceBorder
      )}
    >
      {previewStatus === 'error' ? (
        <div className="flex flex-col items-center gap-3 px-3 py-8 text-center">
          <Text.P className="text-muted-foreground">
            {canRepopulateFromSource
              ? // A warehouse dataset can be rebuilt from its source, so the
                // honest offer is to do that rather than imply a retry will
                // conjure bytes that may be gone.
                'This preview could not be loaded. If it was just updated, retry to load the latest version — or re-populate it from its source.'
              : 'This preview could not be loaded. If the dataset was just updated, retrying usually loads the latest version.'}
          </Text.P>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={handleRetryPreview}
            >
              Retry
            </Button>
            {canRepopulateFromSource ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={repopulating}
                onClick={() => void handleRepopulateFromSource()}
              >
                Re-populate from source
              </Button>
            ) : null}
          </div>
        </div>
      ) : previewAdapter === null ? (
        <DatasetPreviewLoadingGrid
          columns={columns}
          reservedColumns={reservedColumns}
        />
      ) : (
        <DatasetPreviewGridBody
          adapter={previewAdapter}
          canImportCsv={canImportCsv}
          onStatusChange={setPreviewStatus}
          onImportCsv={handleImportCsv}
          readOnly={readOnly}
          layers={layers}
          composedLayers={composedLayers}
          datasetName={datasetName}
          rowCount={rowCount}
          dataRevision={dataRevision}
          {...(columnHeaderActions !== undefined && { columnHeaderActions })}
          {...(onControlsChange !== undefined && { onControlsChange })}
        />
      )}
    </div>
  );
};
